import React from "react";
import PropTypes from "prop-types";
import { LuChevronDown, LuTrash2, LuPencil, LuView, LuVideo } from "react-icons/lu";
import { BiUserVoice } from "react-icons/bi";
import SceneAudioService from "../../../services/sceneAudioService";
import VideoModal, { readJobsIndex, statusColor } from "./VideoModal";
import IdealStudio from "../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import { createBox } from "../../../../common/common_tools";
import i18n from "i18next";

const SceneAudioPanel = (props) => {
  const {
    baseUrl,
    trainingId,
    navItems,
    navItemsIds,
    navItemSelected,
    onNarrationSelect,
    onNarrationDelete,
    onSceneOpen,
    onVideoReady,
    onBoxAdded,
    boxes,
    containedViews,
    containedViewSelected,
    boxSelected,
    pluginToolbars,
    uploadFunction,
  } = props;

  const sceneIndex = React.useMemo(() => {
    if (!navItemsIds || !navItemSelected) return null;
    const idx = navItemsIds.indexOf(navItemSelected);
    return idx >= 0 ? idx : null;
  }, [navItemsIds, navItemSelected]);

  const addVideoToScene = React.useCallback(
    (videoUrl) => {
      if (typeof onVideoReady === "function") {
        onVideoReady(videoUrl);
        return;
      }
      if (!onBoxAdded || !videoUrl || !boxes || !navItems) return;
      const pluginName = "EnrichedPlayer";
      const plugin = IdealStudio.Plugins.get(pluginName);
      if (!plugin) return;
      const navItem =
        containedViewSelected && containedViews && containedViews[containedViewSelected]
          ? containedViews[containedViewSelected]
          : navItems[navItemSelected];
      if (!navItem) return;
      const cv =
        containedViewSelected &&
        containedViews &&
        containedViews[containedViewSelected] &&
        isContainedView(containedViewSelected);
      const cvslide = cv && isSlide(navItem.type);
      const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
      const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];
      const initialParams = {
        parent,
        container: 0,
        name: pluginName,
        position: {
          x: randomPositionGenerator(10, 30),
          y: randomPositionGenerator(10, 30),
          type: "absolute",
        },
        id: ID_PREFIX_BOX + Date.now(),
        page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
        url: videoUrl,
      };
      createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
    },
    [
      onVideoReady,
      onBoxAdded,
      boxes,
      navItems,
      navItemSelected,
      containedViews,
      containedViewSelected,
    ]
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [data, setData] = React.useState(null);
  const audioRef = React.useRef(null);
  const [expandedScenes, setExpandedScenes] = React.useState(() => new Set());
  const [currentPlayingId, setCurrentPlayingId] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [expandedTexts, setExpandedTexts] = React.useState(() => new Set());
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
  const [selectedNarrationText, setSelectedNarrationText] = React.useState("");
  const [selectedAudioUrl, setSelectedAudioUrl] = React.useState("");
  const [selectedNavId, setSelectedNavId] = React.useState(null);
  // Per-narration job count map: { [narrationText]: count }  — so badge only appears on the matching row
  const buildJobsByNarration = () => {
    const jobs = readJobsIndex();
    const map = {};
    jobs.forEach((j) => {
      const key = j.narrationText || "";
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  };
  const [activeJobsByNarration, setActiveJobsByNarration] = React.useState(() =>
    buildJobsByNarration()
  );
  React.useEffect(() => {
    const refresh = () => setActiveJobsByNarration(buildJobsByNarration());
    window.addEventListener("storage", refresh);
    const interval = setInterval(refresh, 2000);
    return () => {
      window.removeEventListener("storage", refresh);
      clearInterval(interval);
    };
  }, []);

  const skeletonStyles = (
    <style>{`
      @keyframes scene-audio-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes scene-audio-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `}</style>
  );
  const skeletonWidths = ["100%", "92%", "82%"];

  React.useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const json = await SceneAudioService.getSceneAudios({ baseUrl, trainingId });
        if (!cancelled) {
          setData(json);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || i18n.t("SceneAudio.ErrorRequestFailed") || "Request failed");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, trainingId]);

  const scenes = data && Array.isArray(data.scenes) ? data.scenes : [];

  const handlePlay = (audio) => {
    if (!audio || !audio.filePath) {
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
    }

    // Eğer aynı ses çalıyorsa, pause yap
    if (currentPlayingId === audio.fileId && isPlaying) {
      try {
        audioRef.current.pause();
      } catch (e) {
        // ignore
      }
      setIsPlaying(false);
      setCurrentPlayingId(null);
      return;
    }

    try {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } catch (e) {
      // ignore
    }

    audioRef.current.src = audio.filePath;
    audioRef.current
      .play()
      .then(() => {
        setCurrentPlayingId(audio.fileId);
        setIsPlaying(true);
        audioRef.current.onended = () => {
          setIsPlaying(false);
          setCurrentPlayingId(null);
        };
      })
      .catch(() => {
        // autoplay blocked or other error; ignore silently
      });
  };

  const renderSceneTitle = (scene) => {
    const nav = navItems && navItems[scene.navId];
    if (nav && nav.name) {
      return nav.name;
    }
    return scene.sceneTitle || scene.navId;
  };

  const formatDuration = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "00:00";
    }
    const totalSeconds = Math.max(0, Math.floor(value));
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const toggleSceneExpanded = (navId) => {
    setExpandedScenes((prev) => {
      const next = new Set(prev);
      if (next.has(navId)) {
        next.delete(navId);
      } else {
        next.add(navId);
      }
      return next;
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",

        boxSizing: "border-box",
        gap: 8,
        paddingBottom: 100,
      }}
    >
      {skeletonStyles}
      <VideoModal
        open={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        narrationText={selectedNarrationText}
        audioUrl={selectedAudioUrl}
        trainingId={trainingId}
        idealStudioTrainingId={window.idealStudioTrainingId}
        onVideoReady={addVideoToScene}
        sceneIndex={sceneIndex}
        navId={selectedNavId}
        boxes={boxes}
        navItems={navItems}
        navItemSelected={navItemSelected}
        containedViews={containedViews}
        containedViewSelected={containedViewSelected}
        pluginToolbars={pluginToolbars}
        uploadFunction={uploadFunction}
        onBoxAdded={onBoxAdded}
      />
      <div
        style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#0f172a",
          marginBottom: 4,
          padding: "12px",
        }}
      >
        {i18n.t("SceneAudio.Title") || "Narration Audio"}
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "0 12px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {skeletonWidths.map((width, idx) => (
              <div
                key={idx}
                style={{
                  height: 56,
                  borderRadius: 10,
                  background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
                  backgroundSize: "200% 100%",
                  animation: "scene-audio-shimmer 1.1s ease-in-out infinite",
                  width,
                }}
              />
            ))}
          </div>
        </div>
      ) : error ? (
        <div style={{ fontSize: 12, color: "#b91c1c" }}>
          {i18n.t("SceneAudio.ErrorPrefix") || "Error"} : {error}
        </div>
      ) : scenes.length === 0 ? (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {i18n.t("SceneAudio.NoTrainingAudio") || "No narration audio saved for this training."}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            overflowY: "auto",
            padding: "0 12px",
          }}
          className="dndWindow"
        >
          {scenes.map((scene) => {
            const isActive = navItemSelected && scene.navId === navItemSelected;
            const hasAudios = scene.audios && scene.audios.length > 0;
            const isExpanded = expandedScenes.has(scene.navId);
            return (
              <div
                key={scene.navId}
                style={{
                  borderRadius: 8,
                  border: isActive ? "2px solid #A963F5" : "1px solid #E5E7EB",
                  backgroundColor: isActive ? "#F3E8FF" : "#F9FAFB",
                  padding: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                  onClick={() => toggleSceneExpanded(scene.navId)}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#111827",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={renderSceneTitle(scene)}
                  >
                    {renderSceneTitle(scene)}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSceneExpanded(scene.navId);
                    }}
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: 9999,
                      border: "1px solid transparent",
                      backgroundColor: "transparent",
                      padding: 0,
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                    aria-label={
                      isExpanded
                        ? i18n.t("SceneAudio.Collapse") || "Collapse"
                        : i18n.t("SceneAudio.Expand") || "Expand"
                    }
                  >
                    <LuChevronDown
                      style={{
                        transition: "transform 0.15s ease-out",
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                      size={16}
                    />
                  </button>
                </div>
                {isExpanded && (
                  <>
                    {!hasAudios ? (
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        {i18n.t("SceneAudio.NoSceneAudio") || "No narration audio found."}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                          marginTop: 4,
                        }}
                      >
                        {scene.audios.map((audio) => {
                          const total = formatDuration(audio.duration);
                          const isThisPlaying = currentPlayingId === audio.fileId && isPlaying;
                          const isTextExpanded = expandedTexts.has(audio.fileId);
                          const shouldShowToggle =
                            audio.narrationText && audio.narrationText.length > 80;

                          return (
                            <div
                              key={audio.fileId}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                                padding: 8,
                                borderRadius: 8,
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E5E7EB",
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                            >
                              {audio.narrationText && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "#374151",
                                      maxHeight: isTextExpanded ? "none" : 48,
                                      overflow: isTextExpanded ? "visible" : "hidden",
                                      textOverflow: isTextExpanded ? "clip" : "ellipsis",
                                      flex: 1,
                                    }}
                                    title={audio.narrationText}
                                  >
                                    {audio.narrationText}
                                  </div>
                                  {shouldShowToggle && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setExpandedTexts((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(audio.fileId)) {
                                            next.delete(audio.fileId);
                                          } else {
                                            next.add(audio.fileId);
                                          }
                                          return next;
                                        });
                                      }}
                                      style={{
                                        flexShrink: 0,
                                        width: 20,
                                        height: 20,
                                        borderRadius: 9999,
                                        border: "none",
                                        backgroundColor: "#F3F4F6",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        padding: 0,
                                        color: "#6B7280",
                                        marginTop: 2,
                                      }}
                                      title={
                                        isTextExpanded
                                          ? i18n.t("SceneAudio.HideText") || "Hide text"
                                          : i18n.t("SceneAudio.ShowText") || "Show text"
                                      }
                                    >
                                      <LuChevronDown
                                        size={12}
                                        style={{
                                          transition: "transform 0.15s ease-out",
                                          transform: isTextExpanded
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                    </button>
                                  )}
                                </div>
                              )}
                              <div
                                style={{
                                  marginTop: 4,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePlay(audio);
                                    }}
                                    style={{
                                      flexShrink: 0,
                                      width: 28,
                                      height: 28,
                                      borderRadius: "9999px",
                                      border: "none",
                                      backgroundColor: isThisPlaying ? "#EEF2FF" : "#F3F4F6",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      padding: 0,
                                      color: isThisPlaying ? "#4F46E5" : "#6B7280",
                                    }}
                                    title={
                                      isThisPlaying
                                        ? i18n.t("SceneAudio.Pause") || "Pause"
                                        : i18n.t("SceneAudio.Play") || "Play"
                                    }
                                  >
                                    <BiUserVoice size={18} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setSelectedNarrationText(audio.narrationText || "");
                                      setSelectedAudioUrl(audio.filePath || "");
                                      setSelectedNavId(scene.navId);
                                      setIsImageModalOpen(true);
                                    }}
                                    style={{
                                      flexShrink: 0,
                                      width: 28,
                                      height: 28,
                                      borderRadius: "9999px",
                                      border: "none",
                                      backgroundColor: "#F3F4F6",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      padding: 0,
                                      color: "#6B7280",
                                    }}
                                    title={i18n.t("SceneAudio.Video") || "Video"}
                                  >
                                    <div
                                      style={{
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <LuVideo size={16} />
                                      {(activeJobsByNarration[audio.narrationText || ""] || 0) >
                                        0 && (
                                        <span
                                          style={{
                                            position: "absolute",
                                            top: -6,
                                            right: -6,
                                            backgroundColor: statusColor.processing,
                                            color: "#fff",
                                            borderRadius: "50%",
                                            width: 12,
                                            height: 12,
                                            fontSize: 8,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 700,
                                          }}
                                        >
                                          {activeJobsByNarration[audio.narrationText || ""] > 9
                                            ? "9+"
                                            : activeJobsByNarration[audio.narrationText || ""]}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  {onSceneOpen && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        onSceneOpen(scene.navId);
                                      }}
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "9999px",
                                        border: "none",
                                        backgroundColor: "#F3F4F6",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        padding: 0,
                                        color: "#6B7280",
                                      }}
                                      title={i18n.t("SceneAudio.ViewScene") || "View scene"}
                                    >
                                      <LuView size={14} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      console.log("[SceneAudioPanel] Edit button clicked", {
                                        onNarrationSelectExists: !!onNarrationSelect,
                                        audio,
                                        sceneNavId: scene.navId,
                                      });
                                      if (onNarrationSelect) {
                                        console.log(
                                          "[SceneAudioPanel] Calling onNarrationSelect with:",
                                          {
                                            ...audio,
                                            navId: scene.navId,
                                          }
                                        );
                                        onNarrationSelect({
                                          ...audio,
                                          navId: scene.navId,
                                        });
                                      } else {
                                        console.warn(
                                          "[SceneAudioPanel] onNarrationSelect is not defined"
                                        );
                                      }
                                    }}
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "9999px",
                                      border: "none",
                                      backgroundColor: "#F3F4F6",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      padding: 0,
                                      color: "#6B7280",
                                    }}
                                    title={i18n.t("SceneAudio.Edit") || "Edit"}
                                  >
                                    <LuPencil size={14} />
                                  </button>
                                  {onNarrationDelete && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        // Debug: Narration delete from SceneAudioPanel
                                        // eslint-disable-next-line no-console
                                        console.log("[SceneAudioPanel] delete click", {
                                          sceneNavId: scene.navId,
                                          audioFileId: audio.fileId,
                                          audioFilePath: audio.filePath,
                                          audio,
                                        });
                                        onNarrationDelete({
                                          ...audio,
                                          navId: scene.navId,
                                        });

                                        // Local API listesini de anında güncelle
                                        setData((prev) => {
                                          if (!prev || !Array.isArray(prev.scenes)) return prev;
                                          return {
                                            ...prev,
                                            scenes: prev.scenes.map((s) => {
                                              if (s.navId !== scene.navId) return s;
                                              return {
                                                ...s,
                                                audios: (s.audios || []).filter(
                                                  (a) => a.fileId !== audio.fileId
                                                ),
                                              };
                                            }),
                                          };
                                        });
                                      }}
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "9999px",
                                        border: "none",
                                        backgroundColor: "#F3F4F6",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        padding: 0,
                                        color: "#6B7280",
                                      }}
                                      title={i18n.t("SceneAudio.Delete") || "Delete"}
                                    >
                                      <LuTrash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {onNarrationSelect && (
                      <div style={{ marginTop: 6 }}>
                        <button
                          type="button"
                          onClick={() => {
                            onNarrationSelect({
                              navId: scene.navId,
                              filePath: null,
                              fileName: renderSceneTitle(scene),
                              duration: 1,
                            });
                          }}
                          style={{
                            fontSize: 11,
                            padding: "4px 8px",
                            borderRadius: 9999,
                            border: "1px solid #A963F5",
                            backgroundColor: "#FDF4FF",
                            color: "#7C3AED",
                            cursor: "pointer",
                          }}
                        >
                          {i18n.t("SceneAudio.AddAudio") || "Add audio"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

SceneAudioPanel.propTypes = {
  baseUrl: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  navItems: PropTypes.object,
  navItemsIds: PropTypes.array,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onNarrationSelect: PropTypes.func,
  onNarrationDelete: PropTypes.func,
  onSceneOpen: PropTypes.func,
  onVideoReady: PropTypes.func,
  onBoxAdded: PropTypes.func,
  boxes: PropTypes.object,
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.string,
  boxSelected: PropTypes.any,
  pluginToolbars: PropTypes.object,
  uploadFunction: PropTypes.func,
};

export default SceneAudioPanel;
